import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ServiceReportOperationObjectStatus } from "./utils";
import {
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
} from "@heroicons/react/24/outline";

export const ReportSectionCard = ({
  title,
  description,
  contentPreview,
  icon: Icon,
  status,
  count,
  total,
  unit,
  onClick,
  to,
  className = "",
  showStatus = true,
  showCount = true,
  showProgress = false,
  progressValue = 0,
  progressMax = 100,
  statusText,
  isCompleted = false,
  isRequired = true,
  canEdit = true,
  /** Small row below counts (e.g. warehouse pick progress) */
  footerExtra = null,
}) => {
  const { t } = useTranslation();

  const getStatusColor = () => {
    if (!isRequired) return "text-yellow-600";
    if (isCompleted) return "text-green-600";
    return "text-red-600";
  };

  const getStatusIcon = () => {
    if (!isRequired)
      return <MinusCircleIcon className="h-5 w-5 text-yellow-600" />;
    if (isCompleted)
      return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
    return <XCircleIcon className="h-5 w-5 text-red-600" />;
  };

  const getProgressColor = () => {
    if (progressValue >= progressMax) return "bg-green-500";
    if (progressValue >= progressMax * 0.8) return "bg-yellow-500";
    return "bg-blue-500";
  };

  const cardContent = (
    <div className="space-y-6 bg-white py-6 px-4 sm:p-6 h-full flex flex-col">
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0">
          {Icon && <Icon className="h-12 w-12 text-teal-600" />}
        </div>
        <div className="flex-1 flex flex-col">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {title}
            </h3>
            {showStatus && getStatusIcon()}
          </div>
          <div className="mt-2 flex-1 flex flex-col">
            <p className="text-sm text-gray-500">{description}</p>
            {contentPreview ? (
              <p className="mt-1 text-xs text-gray-600 line-clamp-2 break-words">
                {contentPreview}
              </p>
            ) : null}
            <div className="mt-2 flex items-center gap-4">
              {showCount && count !== undefined && (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                  {count}
                  {total && ` / ${total}`}
                  {unit && ` ${unit}`}
                </span>
              )}
              {statusText && (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor()}`}
                >
                  {statusText}
                </span>
              )}
              {!canEdit && (
                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                  {t("report.sectionCard.viewOnlyHint")}
                </span>
              )}
            </div>
            {footerExtra ? (
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2">{footerExtra}</div>
            ) : null}
            {showProgress && (
              <div className="mt-auto pt-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">
                    {t("report.sectionCard.progress")}
                  </span>
                  <span className="text-sm text-gray-500">
                    {progressValue} / {progressMax}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
                    style={{
                      width: `${Math.min(
                        (progressValue / progressMax) * 100,
                        100
                      )}%`,
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 self-start">
          {canEdit ? (
            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-yellow-400" />
          )}
        </div>
      </div>
    </div>
  );

  const cardClassName = `shadow sm:overflow-hidden sm:rounded-md transition-colors duration-200 h-full ${
    canEdit 
      ? 'cursor-pointer hover:bg-gray-50' 
      : 'cursor-pointer opacity-90 hover:bg-yellow-50'
  } ${className}`;

  // Use Link if 'to' prop is provided, otherwise use div with onClick
  if (to) {
    return (
      <Link
        to={to}
        className={cardClassName}
        onClick={(e) => {
          // Prevent navigation if not editable
          if (!canEdit) {
            e.preventDefault();
          }
        }}
      >
        {cardContent}
      </Link>
    );
  }

  const handleClick = (e) => {
    if (onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }
  };

  return (
    <div
      className={cardClassName}
      onClick={handleClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          e.stopPropagation();
          onClick(e);
        }
      }}
    >
      {cardContent}
    </div>
  );
};
